import { useState, useCallback, useEffect, useRef } from 'react';
import {
  fetchPreviousSupport,
  sumTildelingerLastThreeYears,
  simplifyTildelinger,
  DE_MINIMIS_THRESHOLD_NOK,
  type SimplifiedTildeling,
} from '@shared/services/stotteregisterApi';

export interface DeMinimisResult {
  organizationNumber: string;
  previousSupport: number;
  requestedAmount: number;
  totalAmount: number;
  isOverThreshold: boolean;
  tildelinger: SimplifiedTildeling[];
  checkedAt: string;
}

export function useDeMinimisCheck() {
  const [result, setResult] = useState<DeMinimisResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeRequest = useRef<AbortController | null>(null);

  useEffect(() => () => {
    activeRequest.current?.abort();
    activeRequest.current = null;
  }, []);

  const checkDeMinimis = useCallback(async (orgnr: string, amount: number) => {
    activeRequest.current?.abort();
    activeRequest.current = null;
    setResult(null);
    if (!/^\d{9}$/.test(orgnr) || !Number.isFinite(amount) || amount < 0) {
      setIsLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    activeRequest.current = controller;
    setIsLoading(true);
    setError(null);

    try {
      const tildelinger = await fetchPreviousSupport(orgnr, controller.signal);
      if (controller.signal.aborted) return;
      const previousSupport = sumTildelingerLastThreeYears(tildelinger);
      const totalAmount = previousSupport + amount;
      const isOverThreshold = totalAmount > DE_MINIMIS_THRESHOLD_NOK;

      setResult({
        organizationNumber: orgnr,
        previousSupport,
        requestedAmount: amount,
        totalAmount,
        isOverThreshold,
        tildelinger: simplifyTildelinger(tildelinger),
        checkedAt: new Date().toISOString(),
      });
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(
        err instanceof Error
          ? err.message
          : 'Kunne ikke hente data fra støtteregisteret'
      );
      setResult(null);
    } finally {
      if (activeRequest.current === controller) {
        activeRequest.current = null;
        setIsLoading(false);
      }
    }
  }, []);

  const reset = useCallback(() => {
    activeRequest.current?.abort();
    activeRequest.current = null;
    setResult(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    checkDeMinimis,
    result,
    isLoading,
    error,
    reset,
    threshold: DE_MINIMIS_THRESHOLD_NOK,
  };
}
